import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'

import type { RetorikActivity, HistoryActivity } from '../../models/activityTypes'
import { checkListDisplay } from '../../utils/checkListDisplay'
import { createNewQuestionForTimestamp } from '../../utils/activityUtils'
import { ListStatus } from '../../models/enums'

import ListTemplate from '../Templates/TemplateList'
import ConversationPanelMobile from './ConversationPanel/ConversationPanelMobile'
import ConversationPanelLarge from './ConversationPanel/ConversationPanelLarge'

const ConversationPanel = (): JSX.Element => {
  const dispatch = useDispatch()
  const messageActivities = useSelector((state: RootState) => state.activityReducer.messageActivities)
  const streamingActivities = useSelector((state: RootState) => state.activityReducer.botStreamingActivities)
  const forceMobileView = useSelector((state: RootState) => state.retorikReducer.configuration.forceMobileView)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const listStatus = useSelector((state: RootState) => state.utilsReducer.listStatus)
  const [listActivity, setListActivity] = useState<RetorikActivity | undefined>(undefined)
  const [itemsInConversation, setItemsInConversation] = useState<number>(0)

  const getActivitiesTextMode = (activities: Array<RetorikActivity>, streamingActivities: Record<string, Array<RetorikActivity>>): Array<HistoryActivity> => {
    // processedId contains the ids of the activities already processed (because useActivities gives duplicate activities with different state depending on if it's been spoken or not )
    let processedId: Array<string> = []
    let history: Array<HistoryActivity> = []

    let tempItemsInConversation = 0
    activities.forEach((activity) => {
      if (activity.id && !processedId.includes(activity.id)) {
        processedId = [...processedId, activity.id]
        // Check if this is a question or an answer (questions from the user don't have a replyToId field)
        if (activity.replyToId) {
          const question: HistoryActivity | undefined = history.find((historyItem) => historyItem.question.id === activity.replyToId)
          if (question) {
            if (!question.answers.length || !question.answers.find((item) => item.id === activity.id)) {
              question.answers = [...question.answers, activity]
              tempItemsInConversation++
            }
          } else {
            const newQuestion: RetorikActivity = createNewQuestionForTimestamp(activity)

            history = [
              ...history,
              {
                question: newQuestion,
                answers: [activity]
              }
            ]
            tempItemsInConversation++
          }
        } else {
          // If the activity has a value, it is a need for further informations that don't need to be displayed
          if (!(activity as RetorikActivity).value) {
            // Hack for Bringup Message to be displayed as bot message
            if ((activity as RetorikActivity).label === 'Davi.BringupMessage' && (activity as RetorikActivity).text) {
              const newQuestion: RetorikActivity = createNewQuestionForTimestamp(activity)
              history = [
                ...history,
                {
                  question: newQuestion,
                  answers: [activity]
                }
              ]

              tempItemsInConversation++
            } else {
              const streamingAnswers = streamingActivities[activity.id]
              history = [
                ...history,
                {
                  question: activity,
                  answers: streamingAnswers || []
                }
              ]

              tempItemsInConversation += streamingAnswers?.length || 0
            }
          }
        }
      }
    })

    setItemsInConversation(tempItemsInConversation)

    return history
  }

  const activities = useMemo<Array<HistoryActivity>>(() => {
    return getActivitiesTextMode(messageActivities, streamingActivities)
  }, [messageActivities, streamingActivities])

  /**
   * Check if there is an activity in need of List display in bot's answers.
   */
  useEffect(() => {
    const answers = activities.length ? activities[activities.length - 1].answers : []
    setListActivity(answers.find((activity: RetorikActivity) => checkListDisplay(activity)))
  }, [itemsInConversation])

  useEffect(() => {
    listStatus === ListStatus.closed && setListActivity(undefined)
  }, [listStatus])

  return (
    <React.Fragment>
      {listActivity && !(listStatus === ListStatus.closed) ? (
        <ListTemplate activity={listActivity} onClose={() => dispatch(storeActions.utils.setListStatus(ListStatus.closed))} />
      ) : isMobile || forceMobileView ? (
        <ConversationPanelMobile activities={activities} itemsInConversation={itemsInConversation} />
      ) : (
        <ConversationPanelLarge activities={activities} itemsInConversation={itemsInConversation} />
      )}
    </React.Fragment>
  )
}

export default ConversationPanel
